/**
 * The recipient directory, editable. Role designations only.
 *
 * Every change is written to the database by /api/alerts/directory and the
 * page's evaluation is refetched, so the dispatch timeline, the acknowledgement
 * table and the escalation chain all read the directory as it now stands. The
 * server refuses any name or role that carries a phone number or an email
 * address, and its reason is shown against the row.
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { API_BASE, api } from '../../api/client'
import type { Evaluation } from './shared'
import { C } from '../../design/tokens'

export interface Recipient {
  id: number
  position: number
  name: string
  role: string
  region: string
  tier: number
  tier_label: string
  channels: string[]
  scope: string
  active: boolean
  reason: string | null
  origin: 'default' | 'edited'
}

interface DirectoryPayload {
  site_id: string
  recipients: Recipient[]
  tiers: Record<string, string>
  regions: Record<string, string>
  channels: Record<string, string>
}

type Draft = Pick<Recipient, 'name' | 'role' | 'region' | 'tier' | 'channels'>

async function send(method: string, path: string, body?: unknown): Promise<unknown> {
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const payload = (await response.json()) as { detail?: unknown }
      if (typeof payload.detail === 'string') detail = payload.detail
      else if (Array.isArray(payload.detail)) detail = payload.detail.map((d: { msg?: string }) => d.msg).join('; ')
    } catch {
      /* the body was not JSON; the status line is what there is */
    }
    throw new Error(detail)
  }
  return response.status === 204 ? null : response.json()
}

const inputStyle = {
  background: 'var(--ink-surface-raised)',
  border: '1px solid var(--ink-border)',
  borderRadius: 'var(--r-control)',
  color: 'var(--ink-text)',
  padding: '0 8px',
  width: '100%',
}

function DraftFields({
  draft,
  setDraft,
  meta,
}: {
  draft: Draft
  setDraft: (d: Draft) => void
  meta: DirectoryPayload
}) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: 'minmax(220px, 2fr) minmax(160px, 1fr) 160px 170px' }}>
      <label className="grid gap-1">
        <span className="section-label">Name</span>
        <input className="ctl" style={inputStyle} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
      </label>
      <label className="grid gap-1">
        <span className="section-label">Role designation</span>
        <input className="ctl" style={inputStyle} value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
      </label>
      <label className="grid gap-1">
        <span className="section-label">Region</span>
        <select className="ctl" style={inputStyle} value={draft.region} onChange={(e) => setDraft({ ...draft, region: e.target.value })}>
          {Object.entries(meta.regions).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="grid gap-1">
        <span className="section-label">Tier</span>
        <select
          className="ctl"
          style={inputStyle}
          value={draft.tier}
          onChange={(e) => setDraft({ ...draft, tier: Number(e.target.value) })}
        >
          {Object.entries(meta.tiers).map(([key, label]) => (
            <option key={key} value={key}>
              {key} · {label}
            </option>
          ))}
        </select>
      </label>
      <div className="grid gap-1" style={{ gridColumn: '1 / -1' }}>
        <span className="section-label">Channels</span>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {Object.entries(meta.channels).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2" style={{ fontSize: 'var(--fs-cell)', color: 'var(--ink-text)' }}>
              <input
                type="checkbox"
                checked={draft.channels.includes(key)}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    channels: e.target.checked ? [...draft.channels, key] : draft.channels.filter((c) => c !== key),
                  })
                }
                style={{ accentColor: 'var(--ink-accent)' }}
              />
              {label}
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

export function RecipientEditor({ siteId, evaluation }: { siteId: string; evaluation: Evaluation }) {
  const client = useQueryClient()
  const directory = useQuery<DirectoryPayload>({
    queryKey: ['alerts-directory', siteId],
    queryFn: () => api.raw(`/alerts/directory?site_id=${encodeURIComponent(siteId)}`),
  })
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [confirming, setConfirming] = useState<number | 'reset' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const meta = directory.data
  const recipients = meta?.recipients ?? []
  // Per-run activation (asset owners follow the infrastructure in this run's footprint).
  const forRun = new Map((evaluation.directory ?? []).map((e) => [e.id, e]))

  async function act(fn: () => Promise<unknown>, after?: () => void) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      after?.()
      await client.invalidateQueries({ queryKey: ['alerts-directory', siteId] })
      await client.invalidateQueries({ queryKey: ['alerts-eval'] })
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc))
    } finally {
      setBusy(false)
    }
  }

  const q = `?site_id=${encodeURIComponent(siteId)}`
  const save = () =>
    draft &&
    act(
      () =>
        editing === 'new'
          ? send('POST', `/alerts/directory${q}`, draft)
          : send('PUT', `/alerts/directory/${editing}${q}`, draft),
      () => {
        setEditing(null)
        setDraft(null)
      },
    )
  const move = (index: number, delta: number) => {
    const ids = recipients.map((r) => r.id)
    const [id] = ids.splice(index, 1)
    ids.splice(index + delta, 0, id)
    return act(() => send('POST', `/alerts/directory/reorder${q}`, { ids }))
  }

  if (!meta) return null
  const channelLabel = (key: string) => meta.channels[key] ?? key

  return (
    <div data-testid="recipient-editor">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span style={{ fontSize: 'var(--fs-cell)', color: 'var(--ink-text-dim)' }}>
          {recipients.length} recipients · dispatch order follows tier, then this order
        </span>
        <div className="ml-auto flex gap-1">
          <button
            type="button"
            className="ctl"
            disabled={busy || editing !== null}
            onClick={() => {
              setEditing('new')
              setDraft({ name: '', role: '', region: recipients[0]?.region ?? Object.keys(meta.regions)[0], tier: 2, channels: [] })
              setError(null)
            }}
          >
            Add recipient
          </button>
          {confirming === 'reset' ? (
            <>
              <button
                type="button"
                className="ctl"
                disabled={busy}
                onClick={() => act(() => send('POST', `/alerts/directory/reset${q}`), () => setConfirming(null))}
              >
                Confirm reset
              </button>
              <button type="button" className="ctl" onClick={() => setConfirming(null)}>
                Keep edits
              </button>
            </>
          ) : (
            <button type="button" className="ctl" disabled={busy} onClick={() => setConfirming('reset')}>
              Reset to default
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-2" role="alert" style={{ fontSize: 'var(--fs-cell)', color: C.sevExtreme }}>
          {error}
        </div>
      )}

      {editing === 'new' && draft && (
        <div className="mb-3 grid gap-2 p-3" style={{ border: '1px solid var(--ink-border)', borderRadius: 'var(--r-control)' }}>
          <DraftFields draft={draft} setDraft={setDraft} meta={meta} />
          <div className="flex gap-1">
            <button type="button" className="ctl" disabled={busy} onClick={save}>
              Save recipient
            </button>
            <button type="button" className="ctl" onClick={() => (setEditing(null), setDraft(null), setError(null))}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="data-table w-full" style={{ minWidth: 1000 }}>
          <thead>
            <tr>
              <th className="num">#</th>
              <th>recipient</th>
              <th>region</th>
              <th className="num">tier</th>
              <th>channels</th>
              <th>this run</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {recipients.map((r, index) => {
              const run = forRun.get(r.id)
              const active = run ? run.active : r.active
              const reason = run ? run.reason : r.reason
              if (editing === r.id && draft) {
                return (
                  <tr key={r.id}>
                    <td className="num">{index + 1}</td>
                    <td colSpan={6}>
                      <div className="grid gap-2 py-2">
                        <DraftFields draft={draft} setDraft={setDraft} meta={meta} />
                        <div className="flex gap-1">
                          <button type="button" className="ctl" disabled={busy} onClick={save}>
                            Save
                          </button>
                          <button type="button" className="ctl" onClick={() => (setEditing(null), setDraft(null), setError(null))}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              }
              return (
                <tr key={r.id}>
                  <td className="num">{index + 1}</td>
                  <td>
                    <div style={{ color: 'var(--ink-text)' }}>{r.name}</div>
                    <div style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                      {r.role}
                      {r.origin === 'edited' ? ' · edited' : ''}
                    </div>
                  </td>
                  <td style={{ color: 'var(--ink-text-dim)' }}>{meta.regions[r.region] ?? r.region}</td>
                  <td className="num">{r.tier}</td>
                  <td style={{ color: 'var(--ink-text-dim)' }}>{r.channels.map(channelLabel).join(', ')}</td>
                  <td style={{ fontSize: 'var(--fs-label)', color: active ? 'var(--ink-text-dim)' : 'var(--ink-text-faint)' }}>
                    {active ? 'dispatched' : 'not dispatched'}
                    {reason ? <div>{reason}</div> : null}
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <button type="button" className="ctl" disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label={`Move ${r.name} up`}>
                        Up
                      </button>
                      <button
                        type="button"
                        className="ctl"
                        disabled={busy || index === recipients.length - 1}
                        onClick={() => move(index, 1)}
                        aria-label={`Move ${r.name} down`}
                      >
                        Down
                      </button>
                      <button
                        type="button"
                        className="ctl"
                        disabled={busy || editing !== null}
                        onClick={() => {
                          setEditing(r.id)
                          setDraft({ name: r.name, role: r.role, region: r.region, tier: r.tier, channels: [...r.channels] })
                          setError(null)
                        }}
                      >
                        Edit
                      </button>
                      {confirming === r.id ? (
                        <button
                          type="button"
                          className="ctl"
                          disabled={busy}
                          onClick={() => act(() => send('DELETE', `/alerts/directory/${r.id}${q}`), () => setConfirming(null))}
                        >
                          Confirm remove
                        </button>
                      ) : (
                        <button type="button" className="ctl" disabled={busy} onClick={() => setConfirming(r.id)}>
                          Remove
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
